'use client'
import { RadioGroupItem } from '@paladise/ui/components/ui/radio-group'
import React from 'react'
import {
  CreatorTools,
  creatorToolsMap,
  EnhancedExperience,
  enhancedExperienceMap,
  PlansTitle,
  PlansTypeId,
} from '../constants'
import InfoTooltip from './InfoTooltip'
import { Button } from '@paladise/ui/components/ui/button'
import { cn } from '@paladise/ui/lib/utils'
import { ROUTE_PATH } from 'config/routerPath'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

interface Prop {
  value: string
  amount: string
  type: (typeof PlansTypeId)[keyof typeof PlansTypeId]
  loading: boolean
  isCurrentUserPlan: boolean
  hsaIosSubscription: boolean
  isCancel?: boolean
  onClick: () => void
}

function Plan({
  value,
  amount,
  type,
  loading,
  isCurrentUserPlan,
  hsaIosSubscription,
  isCancel,
  onClick,
}: Prop) {
  const { push } = useRouter()
  const t = useTranslations()

  const items = [
    {
      key: EnhancedExperience['UniversalCredits'],
      hint: enhancedExperienceMap[EnhancedExperience['UniversalCredits']].hint,
      content: t('plans_amount_credits', {
        amount:
          enhancedExperienceMap[EnhancedExperience['UniversalCredits']][type],
      }),
    },
    {
      key: CreatorTools['CreateAdditionalPals'],
      content:
        type === PlansTypeId['Basic']
          ? t('plans_create_1_additional_ai_partner')
          : t('plans_create_amount_additional_ai_partners', {
              avatar_count:
                creatorToolsMap[CreatorTools['CreateAdditionalPals']][type],
            }),
    },
  ]

  return (
    <div className="bg-glass-separators w-full min-w-[266px] rounded-xl p-4">
      <div className="text-label-l1 text-header mb-3 flex justify-between">
        {t(PlansTitle[type])}
        <RadioGroupItem
          size={20}
          value={value}
          id={value}
          className="min768:block hidden size-[20px]"
        />
      </div>

      <div className="text-body text-label-l1 mb-[10px] h-9">
        <span className="text-bigTitle">{`$${amount}`}</span> {t('month')}
      </div>

      {!hsaIosSubscription && (
        <Button
          variant="gray"
          className={cn(
            'text-button-on-primary bg-button-p1 hover:bg-button-p1-hover min768:hidden mb-[10px] w-full rounded-full',
            isCurrentUserPlan && !isCancel && 'bg-label-l3 text-label-l2',
          )}
          loadingClassName="text-button-on-primary"
          loading={loading}
          disabled={loading || (isCurrentUserPlan && !isCancel)}
          onClick={() => {
            if (isCancel) {
              push(ROUTE_PATH.ACCOUNT.INDEX)
              return
            }
            onClick?.()
          }}
        >
          {isCancel
            ? t('resume_plan')
            : isCurrentUserPlan
              ? t('current_plan')
              : t('plans_start_at', { amount })}
        </Button>
      )}

      <ul className="text-label-l2 text-body text-left">
        {items.map(item => {
          return (
            <li
              key={item.key}
              className="text-label-l2 text-md mb-2 flex items-center justify-start"
            >
              <div className="i-ps-check text-primary-p1 mr-1 size-[18px] flex-shrink-0 leading-[18px]" />
              {item.content}
              {item.hint && <InfoTooltip text={t(item.hint)} />}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default Plan
